import { isAxiosError } from 'axios';
import { createLogger } from 'logging';

import {
    BillingInfo,
    PurchaseSubscriptionRequest,
    Subscription,
    SubscriptionApi,
    SubscriptionApiUpdateBillingInfoRequest,
    SubscriptionApiUpdateSubscriptionRequest,
    SubscriptionPurchaseRecord,
    SubscriptionStatus,
} from '@videoblocks/subscription-service-client';

import { BadRequestError } from './BadRequestError';
import { PaymentError } from './PaymentError';
import { ProcessingError } from './ProcessingError';
import { UnexpectedError } from './UnexpectedError';
import { subscriptionApi, subscriptionServiceConfig } from './subscriptionServiceConfig';

const client = new SubscriptionApi(subscriptionServiceConfig);
const logger = createLogger('SubscriptionApi');

const getSubscriptionByUUID = async (uuid: string): Promise<Subscription | null> => {
    try {
        const response = await client.getSubscriptionByUuid({ uuid });
        return response.data ?? null;
    } catch (error) {
        logger.error({ error, uuid }, 'An error occurred while fetching subscription');
        return null;
    }
};

const getSubscriptionsByUserId = async (userId: number): Promise<Subscription[]> => {
    try {
        const response = await client.getSubscriptionsByUserId({ userId });
        return response.data ?? [];
    } catch (error) {
        logger.error({ error, userId }, 'An error occurred while fetching subscriptions for user');
        return [];
    }
};

const getSubscriptionsByEmail = async (email: string): Promise<Subscription[]> => {
    try {
        const response = await client.findSubscriptions({ email });
        return response.data ?? [];
    } catch (error) {
        logger.error({ error, email }, 'An error occurred while fetching subscriptions for email address');
        return [];
    }
};

const purchaseSubscription = async (purchaseInfo: PurchaseSubscriptionRequest): Promise<SubscriptionPurchaseRecord> => {
    try {
        const result = await client.purchaseSubscription({
            purchaseSubscriptionRequest: purchaseInfo,
        });

        return result.data;
    } catch (error: unknown) {
        if (isAxiosError(error)) {
            if (error.response?.status === 402) {
                throw new PaymentError();
            }
            // Other specific error types can be processed here.
        }
        throw new ProcessingError();
    }
};

const updateBillingInfo = async (userId: number, tokenId: string): Promise<BillingInfo | null> => {
    try {
        const request: SubscriptionApiUpdateBillingInfoRequest = {
            userId,
            updateBillingInfoRequest: {
                tokenId,
            },
        };
        const result = await subscriptionApi.updateBillingInfo(request);
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.warn({ error, userId }, 'Invalid billing info update request');
                throw new BadRequestError();
            } else if (error.response?.status === 404) {
                logger.warn({ error, userId }, 'Billing info not found');
                return null;
            } else if (error.response?.status === 402) {
                throw new PaymentError();
            }
        }
        logger.error({ error, userId }, 'An error occurred while updating billing info');
        throw new UnexpectedError();
    }
};

const cancelSubscription = async (uuid: string, reasonCode: string): Promise<Subscription | null> => {
    const request: SubscriptionApiUpdateSubscriptionRequest = {
        uuid,
        updateSubscriptionRequest: {
            status: SubscriptionStatus.Canceled,
        },
    };
    try {
        const response = await client.updateSubscription(request);
        return response.data;
    } catch (error) {
        logger.error(`An error occurred while canceling subscription with uuid ${uuid}.`, error);
    }
    return null;
};

export const SubscriptionsApi = {
    getSubscriptionByUUID,
    getSubscriptionsByUserId,
    getSubscriptionsByEmail,
    purchaseSubscription,
    updateBillingInfo,
    cancelSubscription,
};
